import React from "react";
import { CompactKpiRibbon } from "@/components/shared/CompactKpiRibbon";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Calendar, ChevronDown, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, cn } from "@/lib/utils";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import type { FilterField } from "@/components/shared/FilterBar";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { ReceiptStatus, getStatusConfig, getStatusOptions } from "@/config/status-registry";
import { useConfirm } from "@/providers/ConfirmProvider";

const FILTER_FIELDS: FilterField[] = [
  { key: "receiptId", label: "Mã phiếu", type: "text", placeholder: "PT-..." },
  {
    key: "customerName",
    label: "Khách hàng",
    type: "text",
    placeholder: "Tên KH...",
  },
  { key: "receiptDate", label: "Ngày", type: "date-range" },
  {
    key: "type",
    label: "Loại phiếu",
    type: "combobox-multi",
    options: [
      { value: "INCOME", label: "Thu" },
      { value: "EXPENSE", label: "Chi" },
    ],
  },
  {
    key: "paymentMethod",
    label: "Phương thức",
    type: "combobox-multi",
    options: [
      { value: "TIEN_MAT", label: "Tiền mặt" },
      { value: "CHUYEN_KHOAN", label: "Chuyển khoản" },
      { value: "CAN_TRU", label: "Cấn trừ" },
    ],
  },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: getStatusOptions("ReceiptStatus", false),
  },
];

function fmtDate(d: any) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("vi-VN");
  } catch {
    return "—";
  }
}

const PAYMENT_METHOD_MAP: Record<string, string> = {
  TIEN_MAT: "Tiền mặt",
  CHUYEN_KHOAN: "Chuyển khoản",
  CAN_TRU: "Cấn trừ",
};

const STATUS_MAP = ReceiptStatus;

const TYPE_MAP: Record<string, { label: string; color: string }> = {
  INCOME: { label: "Thu", color: "text-emerald-700" },
  EXPENSE: { label: "Chi", color: "text-rose-600" },
};

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "receiptId", label: "Mã phiếu", width: 14 },
  {
    key: "type",
    label: "Loại",
    width: 12,
    formatter: (_v, r: any) => (r.type === "INCOME" ? "Thu" : "Chi"),
  },
  {
    key: "receiptDate",
    label: "Ngày",
    width: 14,
    format: "date",
    formatter: (_v, r: any) => r.receiptDate || "",
  },
  {
    key: "customerName",
    label: "Khách hàng",
    width: 25,
    formatter: (_v, r: any) => r.customer?.shortName || r.customer?.name || r.customerName || "",
  },
  {
    key: "amount",
    label: "Số tiền",
    width: 18,
    format: "currency",
  },
  {
    key: "paymentMethod",
    label: "Phương thức",
    width: 16,
    formatter: (_v, r: any) => PAYMENT_METHOD_MAP[r.paymentMethod] || r.paymentMethod || "",
  },
  { key: "referenceNumber", label: "Số tham chiếu", width: 18 },
  { key: "notes", label: "Ghi chú", width: 25 },
  {
    key: "status",
    label: "Trạng thái",
    width: 14,
    formatter: (_v, r: any) => STATUS_MAP[r.status]?.label || r.status || "",
  },
];

export default function ReceiptListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isGrouped, setIsGrouped] = React.useState(true);

  const confirm = useConfirm();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const {
    paginatedItems,
    isLoading,
    searchQuery,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "receipts",
    queryFn: (params) => trpcClient.receipts.list.query(params) as any,
    extraParams: dateParams.from
      ? { receiptDateFrom: dateParams.from, receiptDateTo: dateParams.to }
      : {},
  });

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.receipts.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
      await queryClient.invalidateQueries({ queryKey: ["customer-debts"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá thất bại");
    }
  };

  const rowIds = paginatedItems.map((r: any) => r.id);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    allSelected,
    toggleSelectAll,
  } = rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "thu_chi",
    queryFn: trpcClient.receipts.list.query,
    currentFilters: {
      search: searchQuery || undefined,
      ...filterValues,
      ...(dateParams.from ? { receiptDateFrom: dateParams.from, receiptDateTo: dateParams.to } : {}),
    },
    selectedIds,
    idKey: "id",
  });

  const getBulkActions = () => {
    if (!selectedCount) return [];
    return [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
      {
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.receipts.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "phiếu thu",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["receipts"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} phiếu thu đã chọn?`,
          });
        },
      },
    ];
  };

  const totalIncome = React.useMemo(() => {
    return filtered
      .filter((r: any) => r.type === "INCOME")
      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
  }, [filtered]);

  const totalExpense = React.useMemo(() => {
    return filtered
      .filter((r: any) => r.type === "EXPENSE")
      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
  }, [filtered]);

  const cashIncome = React.useMemo(() => {
    return filtered
      .filter((r: any) => r.type === "INCOME" && r.paymentMethod === "TIEN_MAT")
      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
  }, [filtered]);

  const cashExpense = React.useMemo(() => {
    return filtered
      .filter((r: any) => r.type === "EXPENSE" && r.paymentMethod === "TIEN_MAT")
      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
  }, [filtered]);

  const bankIncome = totalIncome - cashIncome;
  const bankExpense = totalExpense - cashExpense;

  return (
    <>
      <ListPageShell
        trashModule="receipts"
        unitLabel="Phiếu Thu / Chi"
        totalItems={totalItems}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        headerFilters={
          <DatePeriodFilter
            period={period}
            setPeriod={setPeriod}
            customFrom={customFrom}
            setCustomFrom={setCustomFrom}
            customTo={customTo}
            setCustomTo={setCustomTo}
          />
        }
        actions={
          <div className="flex items-center gap-2">
            <DateGroupToggleButton
              isGrouped={isGrouped}
              onToggle={() => setIsGrouped(!isGrouped)}
            />
            <ExportImportButtons
              onExport={() => handleExport()}
              exportLoading={isExporting}
              exportOnly
            />
            <Button
              onClick={() => navigate({ to: "/finance/receipts/create" })}
              className="h-9 px-4 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Tạo Thu Chi</span>
            </Button>
          </div>
        }
        showFilter={showFilter}
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        {/* ── Tồn quỹ — 1 dòng (30/10/2026): 4 thẻ cũ cao ~115px, "Tổng tồn quỹ" và "Số dư ròng" trùng số; bảng phiếu lên trên ── */}
        <CompactKpiRibbon
          className="mb-2"
          testIdPrefix="receipt-balances"
          items={[
            {
              key: "total",
              label: "Tồn quỹ",
              clickable: false,
              variant: totalIncome - totalExpense >= 0 ? "emerald" : "rose",
              count: formatMoney(totalIncome - totalExpense),
              tooltip: `Thu: ${formatMoney(totalIncome)} | Chi: ${formatMoney(totalExpense)}`,
            },
            { key: "cash", label: "Tiền mặt", clickable: false, variant: "slate", count: formatMoney(cashIncome - cashExpense), tooltip: `Thu: ${formatMoney(cashIncome)} | Chi: ${formatMoney(cashExpense)}` },
            { key: "bank", label: "Ngân hàng", clickable: false, variant: "blue", count: formatMoney(bankIncome - bankExpense), tooltip: `Thu: ${formatMoney(bankIncome)} | Chi: ${formatMoney(bankExpense)}` },
            { key: "count", label: "Số phiếu", clickable: false, count: filtered.length },
          ]}
        />

        <div className="overflow-x-auto min-h-[50vh] erp-table-scroll">
          <table
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table min-w-[1250px]",
              rowSelection.selectionMode && "apv-select-mode",
            )}
          >
            <thead className="apv-table-header">
              <tr className="border-b bg-slate-50/50">
                <DataTableSelectAllCheckbox
                  rowSelection={rowSelection}
                  className="w-10 px-2 py-2 text-center font-medium text-slate-500"
                />
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[140px] apv-col-code">
                  Mã phiếu
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[70px]">
                  Loại
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[110px]">
                  Ngày
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 min-w-[240px]">
                  Khách hàng
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[140px]">
                  Số tiền
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[130px]">
                  Phương thức
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={i}>
                      <td colSpan={7} className="p-3">
                        <div className="h-6 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
              ) : (
                <DateGroupedTable<any>
                  items={filtered}
                  isGrouped={isGrouped}
                  getDateKey={(r) => extractDateKey(r.receiptDate || r.createdAt)}
                  renderGroupHeader={(group) => {
                    const groupIncome = group.items
                      .filter((r: any) => r.type === "INCOME")
                      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
                    const groupExpense = group.items
                      .filter((r: any) => r.type === "EXPENSE")
                      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
                    const groupNet = groupIncome - groupExpense;
                    const groupIncomeCount = group.items.filter((r: any) => r.type === "INCOME").length;
                    const groupExpenseCount = group.items.filter((r: any) => r.type === "EXPENSE").length;

                    return (
                      <tr
                        key={`group-${group.dateKey}`}
                        className={cn(
                          "border-y font-semibold text-xs transition-colors",
                          group.isToday
                            ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                            : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                        )}
                      >
                        <td colSpan={5} className="py-2 px-3 text-slate-800">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                              onClick={group.toggleCollapse}
                              title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                            >
                              {group.isCollapsed ? (
                                <ChevronRight size={15} />
                              ) : (
                                <ChevronDown size={15} />
                              )}
                            </Button>
                            <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                              {group.formattedDate}
                            </span>
                            <Badge
                              variant="outline"
                              className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0"
                            >
                              {group.count} phiếu
                            </Badge>
                            <div className="flex items-center gap-1 text-[10px]">
                              {groupIncomeCount > 0 && (
                                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1 font-semibold">
                                  {groupIncomeCount} thu
                                </span>
                              )}
                              {groupExpenseCount > 0 && (
                                <span className="bg-rose-50 text-rose-700 border border-rose-200 rounded px-1 font-semibold">
                                  {groupExpenseCount} chi
                                </span>
                              )}
                            </div>
                            {group.isToday && (
                              <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                                HÔM NAY
                              </Badge>
                            )}
                            {group.isCollapsed && (
                              <span className="text-[11px] text-slate-500 font-normal italic">
                                (Đang thu gọn - bấm để xem chi tiết)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap text-xs font-mono">
                            {groupIncome > 0 && (
                              <span className="text-emerald-700 font-bold text-[11px]" title="Tổng Thu trong ngày">
                                + {formatMoney(groupIncome)}
                              </span>
                            )}
                            {groupExpense > 0 && (
                              <span className="text-rose-600 font-bold text-[11px]" title="Tổng Chi trong ngày">
                                - {formatMoney(groupExpense)}
                              </span>
                            )}
                            <span
                              className={cn(
                                "font-black text-[12px] pl-2 border-l border-slate-300",
                                groupNet > 0
                                  ? "text-emerald-800"
                                  : groupNet < 0
                                    ? "text-rose-700"
                                    : "text-slate-800",
                              )}
                              title="Dòng tiền ròng trong ngày"
                            >
                              {groupNet > 0 ? "+" : ""}
                              {formatMoney(groupNet)}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 px-3"></td>
                      </tr>
                    );
                  }}
                  renderPageSummary={(items) => {
                    const pageIncome = items
                      .filter((r: any) => r.type === "INCOME")
                      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
                    const pageExpense = items
                      .filter((r: any) => r.type === "EXPENSE")
                      .reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
                    const pageNet = pageIncome - pageExpense;

                    return (
                      <tr className="bg-slate-100 font-bold border-t-2 border-slate-300 text-xs">
                        <td
                          colSpan={5}
                          className="py-2.5 px-3 text-slate-800 text-right uppercase tracking-wider font-semibold"
                        >
                          Tổng cộng trang ({items.length} phiếu):
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap text-xs font-mono">
                            {pageIncome > 0 && (
                              <span className="text-emerald-700 font-bold text-[11px]">
                                Thu: + {formatMoney(pageIncome)}
                              </span>
                            )}
                            {pageExpense > 0 && (
                              <span className="text-rose-600 font-bold text-[11px]">
                                Chi: - {formatMoney(pageExpense)}
                              </span>
                            )}
                            <span
                              className={cn(
                                "font-black text-[13px] pl-2 border-l border-slate-300",
                                pageNet > 0
                                  ? "text-emerald-800"
                                  : pageNet < 0
                                    ? "text-rose-700"
                                    : "text-slate-800",
                              )}
                            >
                              Ròng: {pageNet > 0 ? "+" : ""}
                              {formatMoney(pageNet)}
                            </span>
                          </div>
                        </td>
                        <td></td>
                      </tr>
                    );
                  }}
                  emptyState={
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-400">
                        Không tìm thấy dữ liệu
                      </td>
                    </tr>
                  }
                  renderRow={(item: any, idx: number) => {
                  const isSelected = selectedIds.has(item.id);
                  const st = STATUS_MAP[item.status] || STATUS_MAP.DRAFT;
                  const pm =
                    PAYMENT_METHOD_MAP[item.paymentMethod] ||
                    item.paymentMethod;
                  const tp = TYPE_MAP[item.type] || {
                    label: item.type || "—",
                    color: "text-slate-600",
                  };

                  return (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      rowSelection={rowSelection}
                      className={`hover:bg-slate-50 transition-colors cursor-pointer ${isSelected ? "bg-indigo-50/30" : ""}`}
                      onClick={() =>
                        navigate({ to: `/finance/receipts/${item.id}` })
                      }
                    >
                      <DataTableRowCheckbox
                        id={item.id}
                        rowSelection={rowSelection}
                        className="px-2 py-2 text-center"
                      />
                      <td className="apv-col-code px-3 py-2">
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-semibold text-slate-900 whitespace-nowrap leading-tight">
                            {item.receiptId}
                          </span>
                          <span
                            className={`inline-flex items-center px-1.5 py-0 min-h-0 h-4 rounded-full text-[9px] font-medium ${st.color}`}
                          >
                            {st.label}
                          </span>
                        </div>
                      </td>
                      <td className={`px-3 py-2 font-bold text-xs ${tp.color}`}>
                        {tp.label}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {fmtDate(item.receiptDate)}
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-medium min-w-[200px] break-words whitespace-normal leading-tight text-[12px]">
                        {item.customer?.shortName ||
                          item.customer?.name ||
                          item.customer?.code ||
                          "—"}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-medium ${item.type === "EXPENSE" ? "text-rose-600" : "text-emerald-700"}`}
                      >
                        {formatMoney(item.amount)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{pm}</td>
                    </DataTableRow>
                  );
                }}
              />
            )}
          </tbody>
          </table>
        </div>
      </ListPageShell>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
